import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CurrencyRupee as CurrencyIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import apiService from '../services/apiService';

const RechargePlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterService, setFilterService] = useState('all');

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    serviceId: '',
    name: 'Unlimited Earnings',
    days: 1,
    price: '',
    originalPrice: '',
    description: '',
    terms: '',
    isPopular: false,
    sortOrder: 0,
    status: 'active',
  });
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(null);

  const fetchServices = async () => {
    try {
      const response = await apiService.getServices();
      if (response.success) {
        setServices(response.data);
      }
    } catch (err) {
      console.error('Fetch services error:', err);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError('');
      const params = filterService !== 'all' ? { serviceId: filterService } : {};
      const response = await apiService.getRechargePlans(params);
      if (response.success) {
        setPlans(response.data);
      }
    } catch (err) {
      setError('Failed to fetch recharge plans');
      console.error('Fetch plans error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterService]);

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        serviceId: plan.service_id,
        name: plan.name || 'Unlimited Earnings',
        days: plan.days,
        price: plan.price,
        originalPrice: plan.original_price || '',
        earningLimit: plan.earning_limit || '',
        description: plan.description || '',
        terms: plan.terms || '',
        isPopular: plan.is_popular === 1,
        sortOrder: plan.sort_order || 0,
        status: plan.status,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        serviceId: services.length > 0 ? services[0].id : '',
        name: 'Unlimited Earnings',
        days: 1,
        price: '',
        originalPrice: '',
        earningLimit: '',
        description: '',
        terms: '',
        isPopular: false,
        sortOrder: 0,
        status: 'active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlan(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!formData.serviceId || !formData.days || !formData.price) {
        setError('Service, days, and price are required');
        setSaving(false);
        return;
      }

      let response;
      if (editingPlan) {
        response = await apiService.updateRechargePlan(editingPlan.id, formData);
      } else {
        response = await apiService.createRechargePlan(formData);
      }

      if (response.success) {
        setSuccess(editingPlan ? 'Plan updated successfully' : 'Plan created successfully');
        handleCloseDialog();
        fetchPlans();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to save plan');
      }
    } catch (err) {
      setError('Failed to save plan');
      console.error('Save plan error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await apiService.deleteRechargePlan(deletingPlan.id);
      if (response.success) {
        setSuccess('Plan deleted successfully');
        setDeleteDialogOpen(false);
        setDeletingPlan(null);
        fetchPlans();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to delete plan');
      }
    } catch (err) {
      setError('Failed to delete plan');
      console.error('Delete plan error:', err);
    }
  };

  const openDeleteDialog = (plan) => {
    setDeletingPlan(plan);
    setDeleteDialogOpen(true);
  };

  // Group plans by service
  const plansByService = plans.reduce((acc, plan) => {
    const serviceName = plan.service_name || 'Unknown Service';
    if (!acc[serviceName]) {
      acc[serviceName] = [];
    }
    acc[serviceName].push(plan);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CurrencyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Recharge Plans
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#FFD700', color: '#000', '&:hover': { bgcolor: '#FFC700' } }}
        >
          Add Plan
        </Button>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FilterIcon />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Service</InputLabel>
            <Select
              value={filterService}
              label="Filter by Service"
              onChange={(e) => setFilterService(e.target.value)}
            >
              <MenuItem value="all">All Services</MenuItem>
              {services.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name || service.service_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Plans Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : plans.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No recharge plans found. Click "Add Plan" to create one.
          </Typography>
        </Paper>
      ) : (
        Object.entries(plansByService).map(([serviceName, servicePlans]) => (
          <Card key={serviceName} sx={{ mb: 3 }}>
            <CardContent sx={{ bgcolor: '#f5f5f5', py: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {serviceName}
              </Typography>
            </CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell>Plan Name</TableCell>
                    <TableCell align="right">Earning Limit (₹)</TableCell>
                    <TableCell align="center">Days</TableCell>
                    <TableCell align="right">Price (₹)</TableCell>
                    <TableCell align="right">Original Price (₹)</TableCell>
                    <TableCell align="center">Popular</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {servicePlans.map((plan) => (
                    <TableRow key={plan.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">{plan.name}</Typography>
                        {plan.description && (
                          <Typography variant="caption" color="textSecondary">
                            {plan.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {plan.earning_limit ? (
                          <Typography fontWeight="bold" color="primary.main">
                            ₹{plan.earning_limit}
                          </Typography>
                        ) : (
                          <Chip label="Unlimited" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${plan.days} ${plan.days === 1 ? 'Day' : 'Days'}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="success.main">
                          ₹{plan.price}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {plan.original_price ? (
                          <Typography
                            sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                          >
                            ₹{plan.original_price}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {plan.is_popular ? (
                          <Chip label="Popular" size="small" color="warning" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={plan.status}
                          size="small"
                          color={plan.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(plan)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(plan)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ))
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPlan ? 'Edit Recharge Plan' : 'Add Recharge Plan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.serviceId}
                  label="Service"
                  onChange={(e) => setFormData({ ...formData, serviceId: e.target.value })}
                >
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name || service.service_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Plan Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Earning Limit (₹)"
                type="number"
                value={formData.earningLimit}
                onChange={(e) => setFormData({ ...formData, earningLimit: e.target.value })}
                inputProps={{ min: 0 }}
                helperText="Max earnings before plan expires (empty = unlimited)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                required
                label="Days"
                type="number"
                value={formData.days}
                onChange={(e) => setFormData({ ...formData, days: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                required
                label="Price (₹)"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Original Price (₹)"
                type="number"
                value={formData.originalPrice}
                onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                inputProps={{ min: 0 }}
                helperText="Show as strikethrough price"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPopular}
                    onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                  />
                }
                label="Mark as Popular"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: '#FFD700', color: '#000', '&:hover': { bgcolor: '#FFC700' } }}
          >
            {saving ? <CircularProgress size={24} /> : editingPlan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recharge Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the plan "{deletingPlan?.name}" ({deletingPlan?.days} days)?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RechargePlansPage;
